import React from 'react';
import Select from 'react-select';
import { PlanSelect } from '../../Coupons/v2/_components/PlanSelect';
import { formatCouponCode } from '../../Coupons/v2/utils';
import { CouponSelect } from '../_components/CouponSelect';
import { CouponRulesSummary } from '../_components/CouponRulesSummary';
import { OfferItemRow } from '../_components/OfferItemRow';
import { useCreateOfferBootstrap } from './create-offer.bootstrap.hook';
import { JdsButton } from '../../../components/jds/JdsButton';
import { jdsSelectProps } from '../../../components/jds/selectProps';
import '../../../components/jds/jds.css';
import { OrderSummaryCard } from '../_components/OrderSummaryCard';
import { PlanDetailsPanel } from '../_components/PlanDetailsPanel';

export default function CreateOffer(props) {
  const offerId = props.match.params.id;
  const {
    notFound,
    fetchingOffer,
    formik,
    plans,
    plansLoading,
    planItems,
    selectedPlan,
    offerSummary,
    couponHasDiscount,
    getCycleTotal,
    paymentMethods,
    paymentMethodsLoading,
    handleAddItem,
    handleItemChange,
    handleRemoveItem,
    handleLinkToCustomerChange,
    handleCancel,
  } = useCreateOfferBootstrap(offerId, props.location.state?.offer);

  if (notFound) {
    return (
      <div className="jds-scope">
        <span>
          Oferta não encontrada. Volte para a listagem e tente novamente.
        </span>
      </div>
    );
  }

  if (fetchingOffer) {
    return (
      <div className="jds-scope">
        <span>Carregando dados da oferta...</span>
      </div>
    );
  }

  const formActions = (
    <div className="d-flex flex-md-row" style={{ gap: '8px' }}>
      <JdsButton variant="outlined" color="neutral" onClick={handleCancel}>
        Cancelar
      </JdsButton>
      <JdsButton type="submit" disabled={formik.isSubmitting}>
        {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
      </JdsButton>
    </div>
  );

  const hasCouponWithoutDiscount =
    !!formik.values.coupon && !!formik.values.planId && !couponHasDiscount;

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="jds-scope d-flex align-items-start"
      style={{ gap: 'var(--space-16)' }}
    >
      <div className="jds-card" style={{ flex: '1 1 600px', minWidth: 0 }}>
        <div className="mt-4">
          <div
            className="d-flex align-content-between justify-content-between"
            style={{ gap: '8px', marginBottom: 'var(--space-32)' }}
          >
            <h1 className="jds-title-lg">
              {offerId ? 'Editar oferta' : 'Criar oferta'}
            </h1>
            {formActions}
          </div>

          <div className="form-group row">
            <div className="col-lg-6">
              <label>Nome da oferta</label>
              <input
                type="text"
                className="form-control"
                style={{ textTransform: 'uppercase' }}
                value={formik.values.name}
                onBlur={formik.handleBlur('name')}
                onChange={(e) =>
                  formik.setFieldValue('name', formatCouponCode(e.target.value))
                }
              />
              <small className="text-muted">
                Use apenas letras, números, &quot;_&quot; ou &quot;-&quot; (sem
                espaços ou acentos) — o texto é convertido automaticamente em
                maiúsculas.
              </small>
              {formik.touched.name && formik.errors.name && (
                <div className="text-danger">{formik.errors.name}</div>
              )}
            </div>
            <div className="col-lg-6">
              <PlanSelect
                plans={plans}
                plansLoading={plansLoading}
                value={formik.values.planId}
                onChange={(id) => formik.setFieldValue('planId', id)}
                error={formik.touched.planId && formik.errors.planId}
              />
            </div>
          </div>

          <div className="form-group row">
            <div className="col-lg-6">
              <label>Método de pagamento</label>
              <Select
                name="paymentMethods"
                isMulti
                isSearchable={false}
                isLoading={paymentMethodsLoading}
                options={paymentMethods}
                placeholder="Selecione..."
                noOptionsMessage={() => 'Nenhum método disponível'}
                loadingMessage={() => 'Carregando métodos de pagamento...'}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.id}
                value={paymentMethods.filter((method) =>
                  formik.values.paymentMethods.includes(method.id),
                )}
                onChange={(options) =>
                  formik.setFieldValue(
                    'paymentMethods',
                    (options || []).map((option) => option.id),
                  )
                }
                {...jdsSelectProps}
              />
              {formik.touched.paymentMethods &&
                formik.errors.paymentMethods && (
                  <div className="text-danger">
                    {formik.errors.paymentMethods}
                  </div>
                )}
            </div>
            <div className="col-lg-6">
              <label>Data de expiração (opcional)</label>
              <input
                type="date"
                className="form-control"
                {...formik.getFieldProps('expirationDate')}
              />
              {formik.touched.expirationDate &&
                formik.errors.expirationDate && (
                  <div className="text-danger">
                    {formik.errors.expirationDate}
                  </div>
                )}
            </div>
          </div>

          <div className="form-group row align-items-center">
            <div className="col-lg-3">
              <label
                htmlFor="linkToCustomer"
                className="d-flex align-items-center mb-0 text-nowrap"
                style={{ gap: 'var(--space-8)' }}
              >
                <input
                  id="linkToCustomer"
                  type="checkbox"
                  name="linkToCustomer"
                  checked={formik.values.linkToCustomer}
                  onChange={handleLinkToCustomerChange}
                />
                Vincular a um cliente?
              </label>
            </div>
            {formik.values.linkToCustomer && (
              <div className="col-lg-3">
                <label htmlFor="customerEmail">E-mail do cliente</label>
                <input
                  id="customerEmail"
                  type="email"
                  className="form-control"
                  {...formik.getFieldProps('customerEmail')}
                />
                {formik.touched.customerEmail &&
                  formik.errors.customerEmail && (
                    <div className="text-danger">
                      {formik.errors.customerEmail}
                    </div>
                  )}
              </div>
            )}
          </div>

          <div className="mt-4">
            <h3 className="jds-title-sm mb-3">Produtos</h3>
            {formik.values.items.length === 0 && (
              <p className="text-muted">Nenhum produto adicionado.</p>
            )}
            {formik.values.items.map((item, index) => (
              <OfferItemRow
                key={item.key}
                item={item}
                planItem={planItems.find((pi) => pi.itemId === item.itemId)}
                discount={offerSummary.itemDiscounts[item.key]}
                selectedIds={formik.values.items
                  .filter((other) => other.key !== item.key && other.itemId)
                  .map((other) => other.itemId)}
                error={
                  formik.submitCount > 0
                    ? formik.errors.items?.[index]?.itemId
                    : undefined
                }
                onChange={(changes) => handleItemChange(item.key, changes)}
                onRemove={() => handleRemoveItem(item.key)}
              />
            ))}
            <JdsButton variant="outlined" onClick={handleAddItem}>
              Adicionar produto à oferta
            </JdsButton>
          </div>
          <hr className="jds-divider" />
          <div className="mt-4">
            <h3 className="jds-title-sm mb-3">Cupom (opcional)</h3>
            <div className="jds-surface">
              <div className="form-group row" style={{ marginBottom: 0 }}>
                <div className="col-lg-4">
                  <CouponSelect
                    value={formik.values.coupon}
                    onChange={(coupon) =>
                      formik.setFieldValue('coupon', coupon)
                    }
                  />
                  <CouponRulesSummary coupon={formik.values.coupon} />
                  {hasCouponWithoutDiscount && (
                    <small
                      className="d-block mt-2"
                      style={{ color: 'var(--fg-warning-stronger)' }}
                    >
                      Este cupom não gera desconto em nenhum ciclo com o plano e
                      os produtos selecionados — confira o plano, os produtos e
                      os ciclos de cada regra.
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>

          {formik.values.planId && (
            <div className="mt-4">
              <OrderSummaryCard
                planName={selectedPlan?.name ?? ''}
                planAmount={selectedPlan?.amount ?? 0}
                items={formik.values.items}
                discountAmount={offerSummary.discountAmount}
                couponCode={formik.values.coupon?.code}
                couponRules={formik.values.coupon?.rules}
                planId={formik.values.planId}
                getCycleTotal={getCycleTotal}
                finalTotal={offerSummary.finalTotal}
              />
            </div>
          )}

          <div
            className="d-flex justify-content-end"
            style={{ marginTop: 'var(--space-24)' }}
          >
            {formActions}
          </div>
        </div>
      </div>
      {selectedPlan && (
        <PlanDetailsPanel
          plan={selectedPlan}
          addonQuantities={formik.values.items.reduce(
            (acc, { itemId, quantity }) =>
              itemId
                ? { ...acc, [itemId]: (acc[itemId] ?? 0) + quantity }
                : acc,
            {},
          )}
        />
      )}
    </form>
  );
}
